/**
 * @module app.um
 */

import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { ActionOptions, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { fireEvents } from 'app/shared/lib/fire-events';
import { confirmation, ConfirmationOptions, IConfirmationDialogResult } from 'app/shared/elements/ess-confirm';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { AuthReloadEvent, IAuthChangeCustomEventDetail, AuthMedicationReloadEvent } from 'app/um/lib/events';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { checkForOverlappingDates, checkForContainingDates } from 'app/um/lib/utilities';
import { getMatcherFunction, MatcherFunction } from 'app/shared/lib/matcher';
import { IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import { IMedicationChange } from 'app/pp/components/update/update-auth-details-step';
import { guid } from 'app/shared/lib/guid';
import { I18N } from 'aurelia-i18n';
import { isNullOrUndefined, mergeObjects } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';

interface CustomMedication extends UM.AuthMedication {
    canBeSelected?: boolean;
    pendingModification?: boolean;
    isNewlyInserted?: boolean;
}

export interface IApplyAuthMedicationPayload {
    auth: UM.Auth;
    medications: UM.AuthMedication[];
}

interface IAuthMedicationEvent { medication: UM.AuthMedication }
export type AuthMedicationEvent = CustomEvent<IAuthMedicationEvent>;

export interface IAuthMedicationsGridOptions {
    showStatusReason: boolean;
    showQtyApproved: boolean;
    showDaysApproved: boolean;
    showApprovedWithRequestedCharges: boolean;
    showWarnings: boolean;
    portalEditMode?: boolean;
    canDeleteMedications?: boolean;
    pendMedicationChanges?: boolean;
    showMinimalQuantityRequested?: boolean;
    showMinimalQuantityApproved?: boolean;
}

export const defaultOptions: IAuthMedicationsGridOptions = {
    showStatusReason: false,
    showQtyApproved: true,
    showDaysApproved: true,
    showApprovedWithRequestedCharges: false,
    showWarnings: true,
    portalEditMode: false,
    canDeleteMedications: true,
    pendMedicationChanges: false,
    showMinimalQuantityRequested: false,
    showMinimalQuantityApproved: false
}

@autoinject()
export class AuthMedicationsGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public authId: string | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public minimal: boolean = false;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public hideBulkApplyDates: boolean = false;

    @bindable.object({ defaultBindingMode: bindingMode.oneTime })
    public options: IAuthMedicationsGridOptions = defaultOptions;

    @bindable.object()
    public drugApprovedCharges: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugRequestedCharges: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugRequestedDays: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugsAuthDate: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugsExpDate: IAuthContextSettingsDetails = {};

    @bindable.object()
    public drugsStatusReason: IAuthContextSettingsDetails = {};

    @bindable.object({ defaultBindingMode: bindingMode.toView })
    public insertedAuthMedicationIds: string[] = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected pageSize: number | undefined = 10;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected pendingMedicationChanges: IMedicationChange[] = [];

    protected id: guid = guid();

    public medications: CustomMedication[] = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    public includeInactive = false;

    protected selectedMedications: UM.AuthMedication[] = [];

    private eventSubscriptions: Array<Subscription> = [];

    protected applyAllStart: Date | null = null;
    protected applyAllEnd: Date | null = null;
    protected allMedicationsNonDeterminate: boolean = true;

    protected authDetailMatcher: MatcherFunction = getMatcherFunction('id');

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $medsTable!: EssTableCustomElement<UM.AuthMedication>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        private readonly queue: TaskQueue,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.reload();

        this.eventSubscriptions.push(
            this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => { this.reload(); }),
            this.events.subscribe(AuthMedicationReloadEvent, (e: AuthReloadEvent) => { this.reload(); })
        );

        // if grid option is missing canDeleteMedications, default to true to match default settings
        if(isNullOrUndefined(this.options.canDeleteMedications)) this.options.canDeleteMedications = true;
    }

    protected detached(): void {
        this.eventSubscriptions.forEach(s => s.dispose());
        this.eventSubscriptions = [];
    }

    ////****************** Events ***************************************************////
    protected editMedication(medication: UM.AuthMedication): void {
        if(medication && this.$element) {
            const detail: IAuthMedicationEvent = { medication: medication };
            fireEvents(this.$element, ['item-edit'], detail);
        }
    }

    protected selectChanged({ detail }: SelectEvent<CustomMedication>): void {
        this.allMedicationsNonDeterminate = detail.items.every((d: UM.AuthMedication) => {
            return d.status?.determination?.status?.determination?.result ? d.status?.determination?.status?.determination?.result === 'none' : true
        });
        // if item is missing - a select all event occurred
        if(!detail.item) {
            // unselect all items
            if(detail.items.length === 0) {
                this.medications.forEach(medication => {
                    const index = this.selectedMedications.findIndex(
                        selected => selected.id === medication.id
                    );
                    if(index !== -1) {
                        this.selectedMedications.splice(index, 1);
                    }
                });
            } else {
                // save all items
                detail.items.forEach(item => {
                    const alreadySelected = this.selectedMedications.some(
                        medication => medication.id === item.id
                    );
                    if(!alreadySelected) {
                        this.selectedMedications.push(item);
                    }
                });
            }
        } else {
            // save selected checkboxes to restore later logic
            const match = detail.items.some(
                item => item.id === detail.item?.id
            );
            // Check for matching id in detail (allows dups and id's correct medication)
            if(match) {
                // add to selectedMedications if not already present
                const alreadySelected = this.selectedMedications.some(
                    medication => medication.id === detail.item?.id
                );
                if(!alreadySelected) {
                    this.selectedMedications.push(detail.item!);
                }
            } else {
                // remove from selectedMedications if it exists based on the id of detail
                const index = this.selectedMedications.findIndex(
                    medication => medication.id === detail.item?.id
                );
                if(index !== -1) {
                    this.selectedMedications.splice(index, 1);
                }
            }
        }
    }

    ////****************** Functions ************************************************////
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadMedications();
    }

    public clear(): void {
        this.medications = [];
    }

    public async reload(): Promise<void> {
        this.medications.splice(0);
        this.reset();
        await this.loadMedications();
    }

    protected hasMedicationDateConflict(authMedication: UM.AuthMedication): boolean {
        if(!authMedication) return false;

        const conflictingDates = (this.medications.filter((d: UM.AuthMedication) => {
            if(d.id === authMedication.id && d.sequence !== authMedication.sequence) {
                if(d && authMedication) {
                    if(d.status?.determination?.status?.determination?.result) {
                        //if determination is denied or void, we don't care about dates for that item
                        switch(d.status.determination?.status.determination.result) {
                            case 'other':
                            case 'denied':
                                return false;
                        }
                    }
                    return checkForOverlappingDates(<UM.AuthMedication>d, <UM.AuthMedication>authMedication);
                }
            }
            return false;
        }));

        return conflictingDates.length > 0;
    }

    protected async applyBulkDates(): Promise<void> {
        if(!this.selectedMedications.length || !this.authId) return;

        const pendingUpdateMedications: UM.AuthMedication[] = this.selectedMedications.map(m => {
            return {
                ...m,
                auth: { id: this.authId },
                authorizedDate: this.applyAllStart,
                expirationDate: this.applyAllEnd
            } as UM.AuthMedication;
        });

        // If Pending Changes, save bulk date changes to pending changes array
        if(this.options.pendMedicationChanges) {
            if(!this.pendingMedicationChanges) this.pendingMedicationChanges = [];
            this.pendingMedicationChanges.push(...pendingUpdateMedications.map(medication => ({ medication, type: ActionOptions.Update as ActionOptions.Update })));
            const lowercaseMedication = this.i18n.tr('um:medication.$').toLowerCase();
            const trKey = pendingUpdateMedications.length > 1 ? 'ui:messages.events.addToPending_plural' : 'ui:messages.events.addToPending';
            this.toaster.info().message(this.i18n.tr(trKey, { what: lowercaseMedication })).show();
            this.applyAllStart = null;
            this.applyAllEnd = null;
            this.selectedMedications.splice(0);
            await this.loadMedications();
        }
        else if(await this.applyDates(pendingUpdateMedications)) {
            if(this.drugsAuthDate.visible) {
                this.toaster.standard.saved('um:medication.authorizedDate.$alt', true, this.selectedMedications.length);
            }
            if(this.drugsExpDate.visible) {
                this.toaster.standard.saved('um:medication.expirationDate.$alt', true, this.selectedMedications.length);
            }
            this.applyAllStart = null;
            this.applyAllEnd = null;
            this.selectedMedications.splice(0);
            if(this.$medsTable) this.$medsTable.selected.splice(0);
        }
        else {
            this.toaster.error();
        }
    }

    protected hasInvalidServiceDate(medication: UM.AuthMedication): boolean {
        if(!medication) return false;

        switch(medication.status?.determination?.status?.determination?.result) {
            case 'other':
            case 'denied':
                return false;
        }

        return !checkForContainingDates(medication.definition?.dateRange, <UM.AuthMedication>medication);
    }

    private reset(): void {
        this.applyAllStart = null;
        this.applyAllEnd = null;
        this.selectedMedications.splice(0);
    }

    private restoreSelected(): void {
        if(!(this.selectedMedications && this.selectedMedications.length > 0)) { return; }
        // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
        this.queue.queueTask(() => {
            this.$medsTable.restoreSelected(this.selectedMedications);
        });
    }

    protected buildIncludesAndFieldsLists(): string[][] {
        // baseline includes
        const includes = [
            'definition',
            'definition.category',
            'definition.type',
            'status'
        ];

        // baseline fields
        const fields = [
            'authorizedDate',
            'definition.brandName',
            'definition.category.description',
            'definition.code',
            'definition.dateRange.end',
            'definition.dateRange.start',
            'definition.description',
            'definition.genericName',
            'definition.labelInformation',
            'definition.type.code',
            'expirationDate',
            'sequence',
            'status.description',
            'status.flag.allowNullDateOfService'
        ];

        // include only if not readonly (data needed in save auth medication modal)
        if(!this.readonly) {
            const notReadonlyIncludes = [
                'status.determination.status',
                'status.transitions',
                'status.transitions.determination.status'
            ];
            const notReadonlyFields = [
                'status.determination.status.determination.result',
                'status.transitions.code',
                'status.transitions.description',
                'status.transitions.determination.status.determination.result'
            ];
            includes.push(...notReadonlyIncludes);
            fields.push(...notReadonlyFields);
        }

        const quantityRequestedFields = [
            'quantity.requested.display',
            'quantity.requested.unit.code',
            'quantity.requested.unit.description',
            'quantity.requested.value'
        ]

        const quantityApprovedFields = [
            'quantity.approved.display',
            'quantity.approved.unit.code',
            'quantity.approved.unit.description',
            'quantity.approved.value'
        ];

        // include only if not minimal-view
        if(!this.minimal) {
            const notMinimalFields = [
                'definition.strength',
                ...quantityRequestedFields
            ]
            includes.push('quantity.requested.unit');
            fields.push(...notMinimalFields);
            if(this.options.showQtyApproved) {
                includes.push('quantity.approved.unit');
                fields.push(...quantityApprovedFields);
            }
        }

        if(this.drugRequestedDays.visible) {
            fields.push('days.requested');
            if(!this.minimal && this.options.showDaysApproved) {
                fields.push('days.approved');
            }
        }
        // If minimal, check if displaying quantity fields
        else {
            if(this.options.showMinimalQuantityRequested) {
                fields.push(...quantityRequestedFields);
            }
            if(this.options.showQtyApproved && this.options.showMinimalQuantityApproved) {
                fields.push(...quantityApprovedFields);
            }
        }

        // include for tooltip if displaying inactive medications
        if(this.includeInactive) {
            const inactiveFields = [
                'metadata.activeChanged.byUser.name.full',
                'metadata.activeChanged.deactivatedReason',
                'metadata.activeChanged.timestamp'
            ];
            includes.push('metadata.activeChanged.byUser');
            fields.push(...inactiveFields);
        }

        // auth subclass configuration-specific includes
        if(this.drugRequestedCharges.visible) fields.push('charge.requested');
        if(this.drugApprovedCharges.visible || (this.options.showApprovedWithRequestedCharges && this.drugRequestedCharges.visible)) fields.push('charge.approved');
        if(this.drugsStatusReason?.visible || this.options.showStatusReason) {
            const statusReasonIncludes = [
                'status.reason',
                'status.reasons',
                'status.transitions.reasons'
            ];
            const statusReasonFields = [
                'status.reason.code',
                'status.reason.description',
                'status.reasons.code',
                'status.reasons.description',
                'status.transitions.reasons.code',
                'status.transitions.reasons.description'
            ];
            includes.push(...statusReasonIncludes);
            fields.push(...statusReasonFields);
        }

        return [includes, fields];
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMedications(): Promise<void> {
        if(!this.authId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: this.includeInactive,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const [includes, fields] = this.buildIncludesAndFieldsLists();

        try {
            this.state.beginLoad();
            const response = await this.service.loadMedicationsByAuth(this.authId, options, includes, fields);
            this.medications = response.results;
            this.resultCount = response.count || 0;

            if(!this.readonly) {

                this.medications.forEach(m => {
                    m.isActive = m.$state == 'active';

                    // Mark pending modified medications
                    if(this.options.pendMedicationChanges && this.pendingMedicationChanges?.length > 0) {
                        const foundDeletedMedicationIndex = this.pendingMedicationChanges.findIndex(change => change.medication.id === m.id && change.type === ActionOptions.Deactivate) ?? -1;
                        // Mark as pending medication to delete
                        if(foundDeletedMedicationIndex !== -1) {
                            m.isActive = false;
                            m.pendingModification = true;
                        } else {
                            // Update displayed medication to reflect the modified pending change values
                            const foundUpdatedMedication = this.pendingMedicationChanges.find(change => change.medication.id === m.id && change.type !== ActionOptions.Deactivate) ?? null;

                            if(foundUpdatedMedication?.medication) {
                                m = mergeObjects(m, <UM.AuthMedication>foundUpdatedMedication.medication);
                                m.pendingModification = true;
                            }
                        }
                    }

                    //only active medications should be selectable
                    m.canBeSelected = !!m.isActive && !m.pendingModification;

                    // Check if medication is in newly inserted medication list
                    m.isNewlyInserted = this.insertedAuthMedicationIds.findIndex(medicationId => medicationId === m.id) !== -1;
                });
                this.restoreSelected();
            }
            else {
                this.medications.forEach(m => {
                    m.isActive = m.$state == 'active';
                });
            }

            fireEvents(this.$element, ['items-loaded']);
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deleteMedication(medication: UM.AuthMedication): Promise<void> {
        let result: IConfirmationDialogResult = {
            reason: '',
            dialogResponse: ConfirmationOptions.OK
        }

        result = await confirmation.showDelete('um:medication.$', 1, true);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        if(!(result.dialogResponse === confirmation.options.OK && !!result.reason)) {
            return;
        }

        if(this.options.pendMedicationChanges) {
            const deleteMedication: UM.AuthMedication = {
                ...medication,
                metadata: {
                    activeChanged: {
                        deactivatedReason: result.reason
                    }
                }
            }
            if(!deleteMedication.auth?.id) deleteMedication.auth = { id: this.authId };
            if(!this.pendingMedicationChanges) this.pendingMedicationChanges = [];
            this.pendingMedicationChanges.push({ medication: deleteMedication, type: ActionOptions.Deactivate });
            const lowercaseMedication = this.i18n.tr('um:medication.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.addToPending', { what: lowercaseMedication })).show();
            await this.loadMedications();
            return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAuthMedication(medication.id!, false, result.reason!);
            isSuccessful = response.isSuccessful;

            await this.reload();
            fireEvents(this.$element, ['item-delete']);
            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true };
            fireEvents(this.$element, ['auth-change'], detail);
            this.$medsTable.deselect(medication);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.deleted('um:medication.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    protected async applyDates(savedMedications: UM.AuthMedication[]): Promise<boolean> {
        if(!this.authId) return false;

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const authorizedDate = this.applyAllStart?.toString();
            const expirationDate = this.applyAllEnd?.toString();

            // strip out unnecessary fields before sending to API
            const updateMedications = savedMedications.map(m => {
                return {
                    id: m.id,
                    authorizedDate: this.applyAllStart,
                    expirationDate: this.applyAllEnd
                } as UM.AuthMedication;
            });

            const payload: IApplyAuthMedicationPayload = {
                auth: { id: this.authId },
                medications: this.selectedMedications.map(m => ({ id: m.id, authorizedDate, expirationDate }))
            };

            const response = await this.service.applyAuthMedications(payload);
            isSuccessful = response.isSuccessful;

            await this.reload();
            fireEvents(this.$element, ['items-loaded']);

            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true };
            fireEvents(this.$element, ['auth-change'], detail);
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
            return isSuccessful;
        }
    }
}
